import { differenceInCalendarDays, differenceInYears, format } from "date-fns";
import { da } from "date-fns/locale";

export const calculateAge = (birthdate: string) => {
  const d = new Date(birthdate);
  if (d.getFullYear() === 9999 || d.getFullYear() === 1753) return "-";
  return differenceInYears(new Date(), d);
};

/**
 *
 * @param date Date on string format
 * @returns Formatted string date, in Danish, e.g. "29. april 1999"
 */
export const stringDateToPPP = (date: string) => {
  const d = new Date(date);
  if (d.getFullYear() === 9999 || d.getFullYear() === 1753) return "-";
  return format(d, "PPP", { locale: da });
};

/**
 *
 * @param date Date on string format
 * @returns Formatted string date, in Danish, e.g. "29. apr. 1999"
 */
export const stringDateToPP = (date: string | null, defaultIfNullish = "-") => {
  if (!date) return defaultIfNullish;
  const d = new Date(date);
  if (d.getFullYear() === 9999 || d.getFullYear() === 1753) {
    return defaultIfNullish;
  }

  return format(d, "PP", { locale: da });
};

/**
 *
 * @param date Date on string format
 * @returns Formatted string date on the format "d.M.yyyy", e.g. 1.10.2022
 */
export const stringDateToP = (date: string) => {
  const d = new Date(date);
  if (d.getFullYear() === 9999 || d.getFullYear() === 1753) return "-";
  return format(d, "d.M.yyyy", { locale: da }).replaceAll("/", ".");
};
/**
 *
 * @param date Date on string format
 * @returns Formatted string date on the format "ddMMyy", e.g. 011022
 */
export const stringDateToddMMyy = (date: string) => {
  const d = new Date(date);
  if (d.getFullYear() === 9999 || d.getFullYear() === 1753) return "-";
  return format(d, "ddMMyy", { locale: da }).replaceAll("/", ".");
};

/**
 *
 * @param date Date on string format
 * @returns String that shows half year, in Danish, e.g. "1. halvår 2018"
 */
export const stringDateToHalvAar = (date: string) => {
  const d = new Date(date);
  const month = d.getMonth();
  const halfyear = month > 5 ? 2 : 1;
  return `${halfyear}. halvår ${d.getFullYear()}`;
};

/**
 *
 * @param date Date on string format
 * @returns String that shows quarter of year, in Danish, e.g. "1. kvartal 2018"
 */
export const stringDateToKvartal = (date: string) => {
  const d = new Date(date);
  const month = d.getMonth();
  const kvartal = Math.floor(month / 3) + 1;
  return `${kvartal}. kvartal ${d.getFullYear()}`;
};

/**
 *
 * @param date Date on string format
 * @returns Formatted string date on the format "yyyy-MM-dd", e.g. 2022-01-10
 */
export const stringDateToyyyyMMdd = (date: string) => {
  const d = new Date(date);
  if (d.getFullYear() === 9999 || d.getFullYear() === 1753) return "-";
  return format(d, "yyyy-MM-dd", { locale: da }).replaceAll("/", ".");
};
/**
 *
 * @param date Date on string format
 * @returns Formatted string date on the format "yyyy-MM-dd", e.g. 2022-01-10
 */
export const dateToyyyyMMdd = (d: Date) => {
  return format(d, "yyyy-MM-dd");
};

/**
 * takes string on format yyyy-mm-dd or yyyy-mm-ddT00:00:00.000Z and turns
 * into array of year, month and date
 */
export const splitDateStringToParts = (date: string) => {
  const datePart = date.indexOf("T") > 0 ? date.split("T")[0] : date;
  return datePart.split("-").length === 3 ? datePart.split("-") : ["", "", ""];
};

export const parseDurationToDate = (durationStr: string): Date => {
  const match = durationStr.match(/PT(\d+)H(\d+M)?/);
  const hours = match ? parseInt(match[1], 10) : 0;
  const minutes = match && match[2] ? parseInt(match[2], 10) : 0;

  const date = new Date();
  date.setHours(hours, minutes, 0, 0);

  return date;
};
export const stringISODateToTime = (date: string, duration: string) => {
  let hours, minutes;

  if (duration === "PT0S") {
    hours = "00";
    minutes = "00";
  } else {
    const timeComponents = duration.replace("PT", "").split("H");
    hours = timeComponents[0];
    minutes = timeComponents[1]?.replace("M", "") || "00";
  }

  const datetime = new Date(
    `${date}T${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}:00`,
  );

  return datetime.toLocaleTimeString("da-DK", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};
export const durationStringToTimeString = (duration: string) => {
  if (!duration.startsWith("PT")) return "Not a duration";
  const hours = duration.split("H")[0].split("T")[1];
  const minutes = duration.split("H")[1].split("M")[0] ?? "00";
  return `${hours.padStart(2, "00")}:${minutes.padStart(2, "0")}`;
};

export const toISOLocal = (d: Date) => {
  const z = (n) => ("0" + n).slice(-2);
  const zz = (n) => ("00" + n).slice(-3);
  let off = d.getTimezoneOffset();
  const sign = off > 0 ? "-" : "+";
  off = Math.abs(off);

  return (
    d.getFullYear() +
    "-" +
    z(d.getMonth() + 1) +
    "-" +
    z(d.getDate()) +
    "T" +
    z(d.getHours()) +
    ":" +
    z(d.getMinutes()) +
    ":" +
    z(d.getSeconds()) +
    "." +
    zz(d.getMilliseconds()) +
    sign +
    z((off / 60) | 0) +
    ":" +
    z(off % 60)
  );
};

export function formatTimeFromDate(date: Date) {
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");

  return `${hours}:${minutes}:${seconds}`;
}

export const stripTime = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/**
 *
 * @param periodeFraDato
 * @param periodeTilDatoInklusiv
 * @returns boolean true if periode is currently active, false if not. Only checks date part, not time.
 */
export const periodeIsActive = (
  periodeFraDato: Date,
  periodeTilDatoInklusiv: Date,
) => {
  const now = new Date();
  return dateIsWithinInterval(now, periodeFraDato, periodeTilDatoInklusiv);
};

/**
 * Checks if date is within interval, false if not. Only checks date part, not time. Date is considered within interval if it is equal to start or end date.
 * @param date Date to check
 * @param startDate Start date of interval
 * @param endDate End date of interval
 * @returns boolean
 */
export const dateIsWithinInterval = (
  date: Date,
  startDate: Date,
  endDate: Date,
) => {
  return (
    differenceInCalendarDays(startDate, date) <= 0 &&
    differenceInCalendarDays(endDate, date) >= 0
  );
};
